<template>
	<div class="time-slots-selector row" :class="{editable}">
		<div v-if="loading" class="row centered p-20"><AppLoader /></div>
		<div v-if="!loading" class="timetable-container">
			<div v-if="times?.length" v-for="(timeSlot, timeIndex) in times" class="timetable" :class="{active: !editable && timeIndex === activeTimeIndex}" @click="clickTime(timeSlot)">
				<div class="row centered space-between">
					<div class="col-10" :class="{'col-10': editable, 'col-12': !editable}">
						<p class="title" :title="timeSlot.name">{{ timeSlot.name }}</p>
						<p class="time">{{ timeSlot.hour_start+':'+timeSlot.minute_start.toString().padStart(2, '0')+' - '+timeSlot.hour_end+':'+timeSlot.minute_end.toString().padStart(2, '0') }}<span class="next-day" :class="{'d-n': !showNewDaySlot(timeSlot)}">+1</span> </p>
					</div>
					<div class="col-2 button-col">
						<AppClickableIcon icon-name="iconX" class="button-remove" :auto-set-active="false" @click.stop="deleteTime(timeIndex)" />
					</div>
				</div>
			</div>
			<div v-else>
				<p>{{getString('no_daily_report_times')}}</p>
			</div>
		</div>
		<div v-if="!loading" class="add-button-container centered">
			<AppButton text="+" outline class="button-add" @click="clickAddTimeSlot" />
		</div>
		<AppBottomSheet v-if="editable && !loading" :force-width="bottomSheetWidth" :active="newTimeBottomSheetActive" @toggling-popup="closeNewTimeSheet">
			<ModalHeader
				:title="getString(activeTimeIndex !== null ? 'edit_time_slot' : 'add_time_slot')"
				:button-text="getString(activeTimeIndex !== null ? 'confirm' : 'add')"
				:button-disabled="disabledModalButton"
				@click-button="confirmNewTimeSlot"
				button-outline
			/>
			<div class="add-time-list" v-if="newTimeBottomSheetActive">
				<AppList :items="newSlotFields">
					<template #list-slot="{item}">
						<div class="list-item-header">
							<p class="list-item-title">{{ item.title }}</p>
							<AppTextInput class="right-item text-wrapper" :placeholder="item.placeholder || item.value" :value="item.value" :type="item.inputType ? item.inputType : 'text'" @changing-text="item.callbackText" @pressed-enter="confirmNewTimeSlot" />
							<span class="next-day" :class="{'d-n': !item?.showBadge || !newTimeToNextDay}">+1</span>
						</div>
					</template>
				</AppList>
			</div>
		</AppBottomSheet>
	</div>
</template>
<script>
import {useUserStore} from "@/console-new/stores";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppList from "@/shared/components/AppList.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppLoader from "@/shared/components/AppLoader.vue";
import TimetablesEditor from "@/console-new/components/shared/TimetablesEditor.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import API from "@/shared/helpers/API";

export default {
	name: "TimesSlotsSlider",
	mixins: [WindowWidth],
	components: {AppTextInput, ModalHeader, AppBottomSheet, AppList, TimetablesEditor, AppLoader, AppClickableIcon, AppButton},
	emits: ['active-time'],
	setup() {
		let userStore = useUserStore();
		let notificationStore = useNotificationStore();
		let languageStore = useLanguageStore();
		return {getString: languageStore.getString, userStore, showNotification: notificationStore.showNotification, languageStore};
	},
	props: {
		editable: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			newTimeBottomSheetActive: false,
			activeTimeIndex: null,
			newSlotFromTime: null,
			newSlotToTime: null,
			loading: false,
			edited: false,
			newSlotName: this.getString('new_time_slot'),
			times: [],
		}
	},
	computed: {
		newTimeToNextDay() {
			return this.newSlotFromTime && this.newSlotToTime && this.newSlotFromTime > this.newSlotToTime;
		},
		disabledModalButton() {
			return !this.newSlotFromTime || !this.newSlotToTime || !this.newSlotName || this.newSlotFromTime === this.newSlotToTime;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		newSlotFields() {
			return [
				{
					title: this.getString("from"),
					textInput: true,
					customSlot: 'list-slot',
					inputType: "time",
					value: this.newSlotFromTime,
					callbackText: v => { this.newSlotFromTime = v; },
				},{
					title: this.getString("to"),
					showBadge: true,
					textInput: true,
					customSlot: 'list-slot',
					inputType: "time",
					value: this.newSlotToTime,
					callbackText: v => { this.newSlotToTime = v; },
				},{
					title: this.getString("name"),
					placeholder: this.getString('new_time_slot'),
					textInput: true,
					customSlot: 'list-slot',
					value: null,
					callbackText: v => { this.newSlotName = v; },
				}
			];
		},
	},
	methods: {
		clickAddTimeSlot() {
			this.newSlotFromTime = null;
			this.newSlotToTime = null;
			this.newSlotName = this.getString('new_time_slot');
			this.activeTimeIndex = null;
			this.newTimeBottomSheetActive = true;
		},
		confirmNewTimeSlot() {
			if(!this.newSlotFromTime || !this.newSlotToTime || !this.newSlotName) {
				this.showNotification('error-fill-fields-continue');
				return;
			}
			this.newTimeBottomSheetActive = false;
			this.edited = true;
			let newTime = {
				hour_start: this.newSlotFromTime.split(':')[0],
				minute_start: this.newSlotFromTime.split(':')[1],
				hour_end: this.newSlotToTime.split(':')[0],
				minute_end: this.newSlotToTime.split(':')[1],
				name: this.newSlotName,
			}
			if(this.activeTimeIndex !== null) {
				let currSlot = this.times[this.activeTimeIndex];
				if(currSlot.daily_report_time_id) {
					newTime.daily_report_time_id = currSlot.daily_report_time_id;
				}
				this.times[this.activeTimeIndex] = newTime;
			} else {
				this.times.push(newTime);
			}
		},
		showNewDaySlot(slot) {
			return slot.hour_start+':' + slot.minute_start > slot.hour_end + ':' + slot.minute_end;
		},
		closeNewTimeSheet(open) {
			if(!open) {
				this.newTimeBottomSheetActive = false;
			}
		},
		clickTime(time) {
			let currIndex = this.times.indexOf(time);
			if(this.activeTimeIndex === currIndex && !this.editable) {
				this.activeTimeIndex = null;
			} else {
				this.activeTimeIndex = currIndex;
			}
			let emittingValue = this.activeTimeIndex === null ? null : this.times[this.activeTimeIndex];
			this.$emit('active-time', emittingValue);
			if(this.editable) {
				this.newSlotFromTime = time.hour_start + ':' + time.minute_start;
				this.newSlotToTime = time.hour_end + ':' + time.minute_end;
				this.newSlotName = time.name;
				this.newTimeBottomSheetActive = true;
			}
		},
		deleteTime(timeIndex) {
			this.times.splice(timeIndex, 1);
			this.edited = true;
		},
		fetchTimes() {
			if(this.loading || !this.userStore?.currBusinessId) {
				return
			}
			this.loading = true;
			this.edited = false;
			this.userStore.getDailyReportTimeSlots(fetchedTimes => {
				this.times = JSON.parse(JSON.stringify(fetchedTimes || null));
				this.loading = false;
			})
		},
		saveTimes(callback) {
			if(this.loading || !this.userStore?.currBusinessId || !this.edited) {
				if(callback) { callback(!this.edited); }
				return
			}
			this.loading = true;
			API.init().updateDailyReportTimes(this.userStore.currBusinessId, JSON.stringify(this.times), response => {
				if(response?.result === 'OK') {
					let lastInsertId = Number(response?.last_insert || 0);
					if(lastInsertId && this.times?.length) {
						for (let i = this.times.length - 1; i < this.times.length; i--) {
							if (this.times[i] && !this.times[i]?.daily_report_time_id) {
								this.times[i].daily_report_time_id = lastInsertId;
							} else {
								break;
							}
						}
					}
					this.userStore.dailyReportTimeSlots = this.times;
				}
				this.loading = false;
				if (callback) {
					callback(response?.result === 'OK');
				}
			});
		},
	},
	mounted() {
		this.fetchTimes();
	},
	watch: {
		'userStore.currBusinessId'() {
			this.fetchTimes();
		},
	}
}
</script>
<style scoped lang="scss">
.time-slots-selector {
	.add-button-container {
		display: none;
		.button-add {
			min-width: 20px;
			height: 45px;

			:deep(.btn-text p) {
				font-size: 26px;
			}
		}
	}

	.timetable-container {
		overflow-x: scroll;
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 12px 0;
		@include hideScrollbar;

		.timetable {
			padding: 2px 16px;
			border-radius: 13px;
			background-color: var(--color-alpha-1);
			font-size: 14px;
			user-select: none;
			position: relative;
			min-width: 110px;
			cursor: pointer;

			&:hover {
				background-color: var(--color-alpha-2);
			}

			&.active {
				border: 1px solid var(--color-primary);
				padding: 1px 15px;
			}

			p.title {
				@include truncateString();
				max-width: 150px;
				font-size: 14px;
				line-height: 14px;
				margin-top: 6px;
			}

			p.time {
				width: fit-content;
				white-space: nowrap;
				font-size: 12px;
				color: var(--color-txt-1);
				position: relative;

				span.next-day {
					@include centered;
					font-size: 10px;
					position: absolute;
					top: -6px;
					right: -9px;
					color: var(--orange);
				}
			}

			.col-2.button-col {
				display: none;
			}
			.button-remove {
				padding: 2px;
				align-items: center;
				display: flex;
			}
		}
	}


	&.editable {
		.add-button-container {
			display: flex;
			width: 80px;
		}

		.timetable-container {
			width: calc(100% - 80px);

			.timetable {
				padding: 2px 8px 2px 12px;
				min-width: 136px;
				&:hover {
					background-color: var(--color-alpha-1);
				}

				p.title {
					max-width: unset;
					font-size: 12px;
					line-height: 14px;
					margin-top: 4px;
				}

				p.time {
					font-size: 14px;
					span.next-day {
						right: -8px;
					}
				}

				.col-2.button-col {
					display: flex;
					@include centered;
				}
			}
		}
	}
}
.add-time-list {

	.list-item-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		width: 100%;
		gap: 8px;

		.right-item {
			margin-left: auto;
		}

		.text-wrapper {
			width: 240px;
		}

		span.next-day {
			@include centered;
			font-size: 12px;
			position: absolute;
			top: 10px;
			right: 180px;
			color: var(--orange);
			transition: right 0.4s;
		}

		@media (max-width: 1250px) {
			.text-wrapper {
				width: 190px;
			}

			span.next-day {
				right: 130px;
			}
		}
	}
}
</style>